import React, { useContext, useEffect, useState } from "react";
import { ToggleLeft, ToggleRight, Layers, GitBranch, Lock } from "lucide-react";
import { VerifyContext } from "app/VerifyContext";
import { CvCategory, listCategories, updateCategory } from "features/cv-processor/api/CvCategoryApi";

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Tab = "categories" | "families";

export default function CvCategoriesPage() {
  const { superAdmin } = useContext(VerifyContext);
  const [allCategories, setAllCategories] = useState<CvCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("categories");

  const categories = allCategories.filter((c) => !c.is_skill_family);
  const families = allCategories.filter((c) => c.is_skill_family);

  const load = async () => {
    try {
      setAllCategories(await listCategories());
    } catch {
      setError("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const switchTab = (tab: Tab) => setActiveTab(tab);

  const toggleActive = async (cat: CvCategory) => {
    try {
      const updated = await updateCategory(cat.id, { is_active: !cat.is_active });
      setAllCategories((prev) => prev.map((c) => (c.id === cat.id ? updated : c)));
    } catch {
      setError("Failed to update.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">CV Categories</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage role categories and skill families used during CV enhancement.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-surface rounded-xl border border-border w-fit">
          <button
            onClick={() => switchTab("categories")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "categories"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Layers size={14} />
            Categories
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
              activeTab === "categories" ? "bg-primary-foreground/20 text-primary-foreground" : "bg-surface-muted text-muted-foreground"
            }`}>
              {categories.length}
            </span>
          </button>
          <button
            onClick={() => switchTab("families")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "families"
                ? "bg-amber-600 text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <GitBranch size={14} />
            Skill Families
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
              activeTab === "families" ? "bg-amber-500/40 text-amber-200" : "bg-surface-muted text-muted-foreground"
            }`}>
              {families.length}
            </span>
          </button>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
            <button onClick={() => setError("")} className="ml-2 underline">dismiss</button>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* CATEGORIES TAB                                                   */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === "categories" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground max-w-lg">
              Role-based skill pools. Skills in an active pool are eligible to be added to a CV during enhancement when they appear as must-have JD requirements. Assign a category to a CV on the CV processor page.
            </p>

            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : categories.length === 0 ? (
              <div className="rounded-2xl border border-border bg-surface/40 p-8 text-center">
                <p className="text-muted-foreground text-sm">No categories defined.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={`rounded-xl border bg-surface/50 p-5 transition-colors ${
                      cat.is_active ? "border-border" : "border-border opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-foreground">{cat.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                              cat.is_active
                                ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                                : "bg-muted text-muted-foreground border border-border"
                            }`}>
                              {cat.is_active ? "active" : "inactive"}
                            </span>
                            {cat.is_system && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-violet-500/15 text-violet-400 border border-violet-500/25">
                                system
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">{cat.skills_pool.length} skills</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-2 mb-1">
                            <Lock size={10} className="text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">Skill pool is read-only — managed via seeder</span>
                          </div>
                          {cat.skills_pool.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {cat.skills_pool.map((skill) => (
                                <span key={skill} className="text-[11px] px-2 py-0.5 bg-surface-muted border border-border rounded text-foreground">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">No skills defined.</p>
                          )}
                        </div>
                        {superAdmin && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => toggleActive(cat)} title={cat.is_active ? "Deactivate" : "Activate"} className="p-1.5 rounded hover:bg-surface-muted transition-colors">
                              {cat.is_active ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} className="text-muted-foreground" />}
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* SKILL FAMILIES TAB                                               */}
        {/* ---------------------------------------------------------------- */}
        {activeTab === "families" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground max-w-lg">
              Technology families where knowing one skill implies the ability to claim another. During enhancement, if a JD requires Vue.js but the CV only has React.js, and both are in the same family, Vue.js is added automatically. Skills are not directly assignable — used for matching only.
            </p>

            {loading ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : families.length === 0 ? (
              <div className="rounded-2xl border border-border bg-surface/40 p-8 text-center">
                <p className="text-muted-foreground text-sm">No skill families defined.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {families.map((cat) => (
                  <div
                    key={cat.id}
                    className={`rounded-xl border bg-surface/50 p-5 transition-colors ${
                      cat.is_active ? "border-amber-900/40" : "border-border opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <GitBranch size={13} className="text-amber-500/60 shrink-0" />
                          <span className="text-sm font-semibold text-foreground">{cat.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            cat.is_active
                              ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                              : "bg-muted text-muted-foreground border border-border"
                          }`}>
                            {cat.is_active ? "active" : "inactive"}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-amber-500/15 text-amber-400 border border-amber-500/25">
                            skill family
                          </span>
                          <span className="text-xs text-muted-foreground">{cat.skills_pool.length} skills</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 mb-1">
                          <Lock size={10} className="text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">Skill pool is read-only — managed via seeder</span>
                        </div>
                        {cat.skills_pool.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {cat.skills_pool.map((skill, i) => (
                              <React.Fragment key={skill}>
                                <span className="text-[11px] px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded text-amber-300">
                                  {skill}
                                </span>
                                {i < cat.skills_pool.length - 1 && (
                                  <span className="text-muted-foreground text-[11px] self-center">↔</span>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-1">No skills defined.</p>
                        )}
                      </div>
                      {superAdmin && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => toggleActive(cat)} title={cat.is_active ? "Deactivate" : "Activate"} className="p-1.5 rounded hover:bg-surface-muted transition-colors">
                            {cat.is_active ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} className="text-muted-foreground" />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
